import React from 'react';
import { motion } from 'framer-motion';
import { Award, ShieldCheck, Scale, User } from 'lucide-react';
import aboutImg from '../assets/about.png';

const About = () => {
  return (
    <section className="relative bg-white py-32 px-8 md:px-16 overflow-hidden border-t border-slate-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 md:gap-20 items-center">
          
          {/* Photo Column - Reduced size from col-span-5 to col-span-4 and added max-width */}
          <div className="lg:col-span-4 relative flex justify-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative w-full max-w-[320px]" // Constraint to make photo smaller
            >
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#0c3754]/10 rounded-2xl z-0" />
              <div className="relative z-10 aspect-[4/5] overflow-hidden rounded-2xl shadow-[0_30px_60px_-15px_rgba(12,55,84,0.15)] bg-slate-100">
                <img 
                  src={aboutImg} 
                  alt="Anshoo Sharma - Founder" 
                  className="w-full h-full object-cover transition-all duration-1000 scale-105 hover:scale-100"
                />
              </div>
              
              {/* Founder Card */}
              <div className="absolute -bottom-8 -right-8 bg-[#0c3754] p-8 rounded-2xl shadow-2xl text-white z-20 hidden md:block">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <User size={18} className="text-white" /> 
                  </div>
                  <div>
                    <h4 className="font-serif text-lg">Anshoo Sharma</h4>
                    <p className="text-[9px] tracking-wider text-white/50 font-bold uppercase">Founder</p>
                  </div>
                </div>
                <div className="h-[1px] w-full bg-white/10 mb-4" />
                {/* Ensuring "as a" remains white like the rest of the text */}
                <p className="text-[10px] text-white/70 italic">
                  Legal & Compliance as a Service
                </p>
              </div>
            </motion.div>
          </div>

          {/* Content Column - Expanded to col-span-8 */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="h-[1px] w-12 bg-[#0c3754]/20" />
                <span className="text-[10px] uppercase tracking-widest text-[#0c3754] font-bold">About LCaaS</span>
              </div>

              {/* H2 Heading "A Legacy of Integrity..." has been removed as requested */}

              <div className="space-y-8 text-slate-600 text-sm leading-relaxed">
                <p className="text-lg text-[#0c3754]/80 font-medium">
                  Anshoo Sharma, founder of LCaaS is a Lawyer and Company Secretary (AIR-14) in India and have obtained certifications: ISO 27001; ISO 22301 and Foundation of Risk Management (GARP).
                </p>
                <p>
                  After over two decades of experience across legal, regulatory and compliance functions in the financial services sector, she has commenced an independent advisory practice under the platform LCaaS – Legal & Compliance as a Service. 
                  {/* Note: In the paragraph above, "as a" uses the same styling as the surrounding text for uniformity */}
                </p>
                <p>
                  She has experience spanning organizations such as Fox Mandal, Max Life Insurance, Canara HSBC Life Insurance, and Airtel Payments Bank. Her work involved regular engagement with regulatory bodies including Reserve Bank of India (RBI), Insurance Regulatory and Development Authority of India (IRDAI), Ministry of Home Affairs (MHA), Financial Intelligence Unit (FIU) and National Payments Corporation of India (NPCI).
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                {[
                  { icon: <Award size={18} />, label: "Experience", val: "25+ Years" },
                  { icon: <ShieldCheck size={18} />, label: "Expertise", val: "Ex Chief Vigilance Officer" },
                  { icon: <Scale size={18} />, label: "Specialization", val: "Insurance and Banking Expert" },
                ].map((stat, i) => (
                  <div key={i} className="group p-6 border border-slate-100 rounded-2xl hover:border-[#0c3754]/10 hover:bg-[#e3f4ff]/20 transition-all duration-500">
                    <div className="text-[#0c3754]/30 group-hover:text-[#0c3754] transition-colors mb-3">
                      {stat.icon}
                    </div>
                    <div className="text-[11px] font-bold text-[#0c3754] uppercase tracking-wider mb-1">{stat.val}</div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;